import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { TourFlags } from 'src/app/core/models/auth.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { snakeToCamel } from 'src/app/core/utils/string.utils';

@Injectable({ providedIn: 'root' })
export class TourFlagService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Retorna as flags do AuthUser em cache local (sessionStorage).
   * Não faz requisição — as flags chegam no payload de login (DT-02).
   */
  getFlags(): TourFlags | null {
    return this.authService.getUser()?.tourFlags ?? null;
  }

  /**
   * Atualiza uma flag de tour no backend e reflete no cache local do AuthUser.
   *
   * @param flag  Nome snake_case da flag conforme enum TourFlag no backend
   *              Exemplo: 'tour_home_concluido', 'primeiro_login', 'menu_org_pulsando'
   * @param valor Novo valor booleano da flag
   */
  marcarFlag(flag: string, valor: boolean): Observable<void> {
    return this.http
      .patch<void>(`${this.baseUrl}/api/v1/tour/flags`, { flag, valor })
      .pipe(
        tap(() => this.atualizarCacheLocal(flag, valor))
      );
  }

  /**
   * Atalho para marcar a flag de conclusão de um tour como true.
   */
  marcarConcluido(flag: string): Observable<void> {
    return this.marcarFlag(flag, true);
  }

  /**
   * Zera a flag de um tour para permitir revisão (usado em "Rever tour").
   */
  zerarFlag(flag: string): Observable<void> {
    return this.marcarFlag(flag, false);
  }

  /**
   * Verifica se uma flag de conclusão está marcada no cache local.
   * Retorna false quando o backend ainda não respondeu (flags ausentes = tour não concluído).
   */
  isConcluido(flag: keyof TourFlags): boolean {
    const flags = this.getFlags();
    if (!flags) return false;
    return !!flags[flag];
  }

  // ─── Cache local ────────────────────────────────────────────────────────────

  /**
   * Converte o nome snake_case do backend para o camelCase do TourFlags do frontend
   * e atualiza o AuthUser no sessionStorage.
   *
   * Exemplos de mapeamento:
   *   tour_home_concluido           → tourHomeConcluido
   *   tour_org_estrutura_concluido  → tourOrgEstruturaConcluido
   *   primeiro_login                → primeiroLogin
   *   menu_org_pulsando             → menuOrgPulsando
   */
  private atualizarCacheLocal(flagSnake: string, valor: boolean): void {
    const user = this.authService.getUser();
    if (!user) return;

    const flagCamel = snakeToCamel(flagSnake) as keyof TourFlags;
    const flagsAtuais = user.tourFlags ?? ({} as TourFlags);

    this.authService.setUser({
      ...user,
      tourFlags: {
        ...flagsAtuais,
        [flagCamel]: valor,
      },
    });
  }

}
